/**
 * ★★ PC-2 — O SISTEMA CONCLUI SOZINHO O PRÓXIMO CONTATO QUE O GESTO TORNOU VELHO. PURO.
 *
 * Decisões do dono (17/out/2026, `docs/pc2-plano.md`):
 *
 *  1. ★ **agendou** (transição para `agendado`) e **fechou** fecham TODAS as abertas do lead
 *     ("por lead", não por responsável).
 *  2. ★ "agendou" vale só na TRANSIÇÃO: 2ª consulta ou remarcar com o lead JÁ agendado não
 *     fecha nada — é o que deixa o lembrete criado depois de agendar sobreviver.
 *  3. ★ **novo contato** fecha só as abertas do MESMO responsável efetivo
 *     (`responsavelId`, ou o SDR do lead quando a tarefa não tem dono gravado).
 *
 * ⚠ Este módulo só DECIDE. Quem lê (do servidor) e grava (no mesmo lote do gesto) é o
 * `tarefasService` — e a leitura é sempre ANTES do lote, por isso a tarefa nova do próprio
 * gesto nunca chega aqui; `excetoIds` é a segunda barreira, não a primeira.
 *
 * ⚠⚠ Nada de `ultimoContatoEm` nem de carimbo do lead aqui: o sistema concluir não é
 * contato com o paciente. Há guarda de fonte (G5).
 */

export const MOTIVOS_CONCLUSAO = ['agendou', 'fechou', 'novo_contato'];

/** Marca gravada em `concluidaPorId` — a mesma que `autoria(null)` usa para "o sistema". */
export const AUTOR_SISTEMA = { id: 'sistema', nome: 'Sistema' };

const motivoValido = (motivo) => {
  if (!MOTIVOS_CONCLUSAO.includes(motivo)) throw new Error(`Motivo de conclusão desconhecido: ${motivo}`);
};

/**
 * Quais abertas o gesto fecha.
 *
 * @param abertas  as tarefas lidas do servidor (`{ id, concluida, responsavelId, … }`)
 * @param motivo   'agendou' | 'fechou' | 'novo_contato'
 * @param responsavelNovo  o dono da tarefa que o gesto está criando (só em novo_contato)
 * @param sdrDoLead        herança do dono quando a aberta não tem `responsavelId`
 * @param excetoIds        ids criados NESTE lote — nunca fecham
 */
export function abertasAFechar(abertas, { motivo, responsavelNovo = '', sdrDoLead = '', excetoIds = [] } = {}) {
  motivoValido(motivo);
  const exceto = new Set(excetoIds);
  const candidatas = (abertas ?? []).filter((t) => t?.id && t.concluida !== true && !exceto.has(t.id));
  if (motivo !== 'novo_contato') return candidatas;
  // ⚠ Conservador: sem dono novo conhecido, ou aberta sem dono resolvível, NÃO fecha.
  if (!responsavelNovo) return [];
  return candidatas.filter((t) => {
    const dono = t.responsavelId || sdrDoLead || '';
    return dono !== '' && dono === responsavelNovo;
  });
}

/**
 * Os campos do `update` de cada tarefa encerrada pelo sistema.
 * `carimbo` é injetado (em produção, `serverTimestamp`) para o módulo ficar puro.
 */
export function camposDaConclusaoAutomatica(motivo, autor, { carimbo } = {}) {
  motivoValido(motivo);
  if (typeof carimbo !== 'function') throw new Error('camposDaConclusaoAutomatica: carimbo é obrigatório.');
  return {
    concluida: true,
    concluidaEm: carimbo(),
    concluidaPorId: AUTOR_SISTEMA.id,
    concluidaPorNome: AUTOR_SISTEMA.nome,
    concluidaMotivo: motivo,
    // ★ Quem fez o gesto que tornou a tarefa velha — a autoria humana fica rastreável.
    concluidaNoGestoDe: autor?.id ?? AUTOR_SISTEMA.id,
  };
}

/** Decisão 2: "agendou" só fecha quando a etapa ANTES não era `agendado`. */
export const disparaAgendou = (etapaAntes) => etapaAntes !== 'agendado';

/**
 * O sufixo da linha da timeline que o gesto já grava — nenhuma interação nova.
 *
 * ⚠ Não pode conter "agendad": `produtividade/dia.js` classifica linhas `tipo:'tarefa'` por
 * `/reagendado/` e `/agendado/`, e um sufixo assim mudaria a contagem do painel (P7/G6).
 */
export function sufixoDaConclusao(n) {
  const qtd = Number(n) || 0;
  if (qtd <= 0) return '';
  if (qtd === 1) return ' — 1 próximo contato anterior encerrado pelo sistema';
  return ` — ${qtd} próximos contatos anteriores encerrados pelo sistema`;
}
